import {
  cn,
  formatDate,
  getStatusBorderColor,
  getStatusColor,
  Threat,
  ThreatsActivity,
  TimeFormats,
} from "@theprelude/core";
import {
  Button,
  CalendarIcon,
  CopyButton,
  ExternalLinkIcon,
  OverlayInfoBlock,
  TrendingIcon,
  Truncate,
  UserIcon,
  VSTIndicatorIcon,
} from "@theprelude/ds";
import { useOverlay } from "@/hooks/use-overlay";
import { ExternalLink } from "../external-link";
import { Flag } from "../flag/flag";
import {
  BenchmarkPercent,
  NonApplicablePercent,
  ProtectedPercent,
} from "../protection-blocks/protection-blocks";
import ThreatActivityTable, { ThreatActivityEntry } from "./threat-overlay";

interface ViewThreatProps {
  threat: Threat;
  threatActivity: ThreatsActivity | undefined;
  rows: ThreatActivityEntry[];
}

export default function ViewThreat({
  threat,
  threatActivity,
  rows,
}: ViewThreatProps) {
  const overlay = useOverlay();

  const sourceId = threat.source_id?.toLocaleUpperCase();
  return (
    <>
      <div className="flex flex-col gap-4 p-4">
        <div className="flex items-center gap-6 truncate border-stroke-line border-b pb-4">
          {sourceId && (
            <>
              <div className="flex flex-col gap-1">
                <span className="text-secondary-foreground text-xs">
                  Source ID
                </span>
                <span className="flex max-w-64 items-center gap-1 text-base">
                  <TrendingIcon className="fill-status-red" />{" "}
                  <Truncate text={sourceId}>{sourceId}</Truncate>
                  {threat.source && <ExternalLink href={threat.source} />}
                </span>
              </div>
              <div className="h-[44px] border-stroke-line border-r" />
            </>
          )}
          {threat.published && (
            <>
              <div className="flex flex-col gap-1">
                <span className="text-secondary-foreground text-xs">
                  Published
                </span>
                <span className="text-base">
                  {formatDate(
                    new Date(threat.published),
                    TimeFormats.DATE_LONG,
                  )}
                </span>
              </div>
              <div className="h-[44px] border-stroke-line border-r" />
            </>
          )}

          {threat.author && (
            <div className="flex flex-col gap-1">
              <span className="text-secondary-foreground text-xs">Author</span>
              <span className="flex items-center gap-1 text-base">
                <UserIcon className="text-secondary-foreground" />
                <span className="max-w-40 truncate pr-1">{threat.author}</span>
                {threat.account_id === "prelude" && <VSTIndicatorIcon />}
              </span>
            </div>
          )}
        </div>

        <h2 className="text-3xl leading-9">{threat.name}</h2>

        <OverlayInfoBlock title="Threat ID">
          <div className="flex items-center gap-1">
            {threat.id}
            <CopyButton toCopy={threat.id} />
          </div>
        </OverlayInfoBlock>

        <div className="flex items-center gap-6">
          <Flag
            features={["observed_detected"]}
            fallback={<ProtectedPercent percent={threatActivity?.protected} />}
          >
            <ProtectedThreatPercent activity={threatActivity} />
          </Flag>

          {threat.author === "Prelude" && (
            <BenchmarkPercent social={threatActivity?.social} />
          )}
        </div>

        <div className="flex gap-4">
          {threat.source && (
            <a
              target="_blank"
              href={threat.source}
              rel="noreferrer"
              className="inline-flex text-secondary-foreground hover:text-primary-foreground"
            >
              <Button
                intent="secondary"
                icon={<ExternalLinkIcon />}
                iconPosition="right"
              >
                Read summary
              </Button>
            </a>
          )}

          <Button
            intent="primary"
            icon={<CalendarIcon />}
            onClick={() => {
              void overlay.open("schedule", {
                type: "threat",
                defaultSelectedId: threat.id,
              });
            }}
          >
            Schedule
          </Button>
        </div>
      </div>

      <div className="border-stroke-line border-t" />
      <ThreatActivityTable activity={rows} />
    </>
  );
}

const ProtectedThreatPercent: React.FC<{
  activity: ThreatsActivity | undefined;
}> = ({ activity }) => {
  if (!activity || activity.prelude_total === 0) {
    return <NonApplicablePercent />;
  }

  const percent = Math.round(
    (activity.prevented / activity.prelude_total) * 100,
  );
  const textColor = getStatusColor(percent);
  const borderColor = getStatusBorderColor(percent);

  return (
    <div
      className={cn(
        "flex w-max flex-col border border-stroke-line p-4",
        textColor,
        borderColor,
      )}
    >
      <span className="font-medium text-sm">Protected</span>
      <span className="text-4xl">{percent}%</span>
      <span className="text-primary-foreground text-xs">
        {activity?.endpoints} endpoints tested
      </span>
    </div>
  );
};
